import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { StudioIcon } from '@atlaskit/logo';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { getStudioPath } from '../../../common/utils/atlassian-studio-url/index.tsx';
import { AgentCreateButton } from '../../../controllers/agent-create-button/index.tsx';
import { useAgentSitePermissions } from '../../../controllers/agent-site-permissions/index.tsx';

import messages from './messages.ts';

const styles = cssMap({
	headerWrapper: {
		width: '100%',
		paddingTop: token('space.300'),
		backgroundColor: token('elevation.surface'),
	},

	header: {
		flexWrap: 'wrap',
		width: '100%',
		justifyContent: 'space-between',
		gap: token('space.150'),
		'@media (min-width: 48rem)': {
			flexWrap: 'nowrap',
		},
	},
	titleContainer: {
		alignItems: 'start',
		gap: token('space.100'),
	},
});

type Props = {
	siteId: string;
};

export const BrowseAgentsFullscreenHeader = ({ siteId }: Props): React.JSX.Element => {
	di(useAgentSitePermissions);

	const { email } = useProfileController();
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={styles.headerWrapper}>
			<Inline space="space.200" alignBlock="start" xcss={styles.header}>
				<Stack alignBlock="start" xcss={styles.titleContainer}>
					<Heading as="h1" size="large">
						{formatMessage(messages.headerTitle)}
					</Heading>
					<Text>{formatMessage(messages.headerSubtitle)}</Text>
				</Stack>

				<Inline space={'space.100'}>
					<AgentCreateButton
						siteId={siteId}
						buttontext={messages.createAgentButton}
						touchpointSource={'browse-agents-fullscreen-header'}
						analyticsPayload={{
							actionSubjectId: 'browseAgentsCreateAgentButtonRefresh',
							attributes: {
								interactionSource: 'browse-agents-fullscreen-header',
							},
						}}
					/>
					<LinkButton
						iconBefore={(props) => <StudioIcon {...props} size="xxsmall" />}
						href={getStudioPath(`/s/${siteId}`, email)}
						target="_blank"
					>
						{formatMessage(messages.gotoStudioButton)}
					</LinkButton>
				</Inline>
			</Inline>
		</Flex>
	);
};
